import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Home, CheckCircle2, XCircle, Shield, Star, Edit2, Trash2, Tag, MapPin, ChevronDown, ChevronUp, Search, Filter, PauseCircle, PlayCircle, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { API, resolveImg, getToken } from './shared';
import ListingDetailPanel from './ListingDetailPanel';

const HomepageOrderPanel = ({ onClose }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API}/admin/homepage-listings`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setListings(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchOrder(); }, []);

  const move = (idx, dir) => {
    const newList = [...listings];
    const target = idx + dir;
    if (target < 0 || target >= newList.length) return;
    [newList[idx], newList[target]] = [newList[target], newList[idx]];
    setListings(newList);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/homepage-reorder`, { order: listings.map(l => l.id) }, { headers: { Authorization: `Bearer ${getToken()}` } });
      alert('Homepage order saved!');
    } catch (err) { alert('Failed to save order'); }
    setSaving(false);
  };

  if (loading) return <div className="p-6 text-center text-slate-500">Loading...</div>;

  return (
    <div className="bg-white border border-blue-200 rounded-2xl mb-6 overflow-hidden shadow-sm" data-testid="homepage-order-panel">
      <div className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-200">
        <h3 className="font-semibold text-blue-900 flex items-center gap-2"><Home className="w-4 h-4" /> Homepage Listing Order</h3>
        <div className="flex items-center gap-2">
          <button onClick={saveOrder} disabled={saving} className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-all" data-testid="save-homepage-order">{saving ? 'Saving...' : 'Save Order'}</button>
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors">Close</button>
        </div>
      </div>
      <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
        {listings.map((listing, idx) => (
          <div key={listing.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 transition-colors" data-testid={`homepage-item-${idx}`}>
            <span className="text-sm font-bold text-slate-400 w-6 text-center">{idx + 1}</span>
            {listing.images?.[0] && (
              <img src={resolveImg(listing.images[0])} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{listing.title}</p>
              <p className="text-xs text-slate-500">{listing.locality}, {listing.city}</p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-20 transition-all" data-testid={`move-up-${idx}`}><ArrowUp className="w-4 h-4" /></button>
              <button onClick={() => move(idx, 1)} disabled={idx === listings.length - 1} className="p-1.5 rounded-lg hover:bg-slate-200 disabled:opacity-20 transition-all" data-testid={`move-down-${idx}`}><ArrowDown className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {listings.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No homepage listings. Use "Add to Homepage" on listings below.</p>}
      </div>
    </div>
  );
};

const AllListingsTab = ({ allListings: initialListings, onRefresh, onEditListing }) => {
  const [allListings, setAllListings] = useState(initialListings);
  const [selectedAll, setSelectedAll] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [expandedListing, setExpandedListing] = useState(null);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [propertyTypeFilter, setPropertyTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showHomepageOrder, setShowHomepageOrder] = useState(false);

  useEffect(() => {
    setAllListings(initialListings);
  }, [initialListings]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchFiltered();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchText, cityFilter, propertyTypeFilter, dateFrom, dateTo]);

  const fetchFiltered = async () => {
    try {
      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);
      if (cityFilter) params.append('city', cityFilter);
      if (propertyTypeFilter) params.append('property_type', propertyTypeFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const res = await axios.get(`${API}/admin/listings/all?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setAllListings(res.data);
    } catch (error) { console.error('Error fetching listings:', error); }
  };

  const handleAction = async (action, id, params = '') => {
    try {
      await axios.put(`${API}/admin/listings/${id}/${action}${params}`, {}, { headers: { Authorization: `Bearer ${getToken()}` } });
      fetchFiltered();
    } catch (error) { console.error(`Error ${action}:`, error); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    try {
      await axios.delete(`${API}/admin/listings/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      fetchFiltered();
    } catch (error) { console.error('Error deleting:', error); }
  };

  const handleBulkAction = async (action) => {
    if (selectedAll.size === 0) return;
    if (action === 'delete' && !window.confirm(`Are you sure you want to permanently delete ${selectedAll.size} listing(s)? This cannot be undone.`)) return;
    setBulkLoading(true);
    try {
      await axios.post(`${API}/admin/listings/bulk-${action}`, { listing_ids: Array.from(selectedAll) }, { headers: { Authorization: `Bearer ${getToken()}` } });
      setSelectedAll(new Set());
      fetchFiltered();
    } catch (error) { console.error(`Error bulk ${action}:`, error); }
    finally { setBulkLoading(false); }
  };

  const toggleSelect = (id) => {
    setSelectedAll(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const clearFilters = () => {
    setSearchText('');
    setCityFilter('');
    setPropertyTypeFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const hasFilters = searchText || cityFilter || propertyTypeFilter || dateFrom || dateTo;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
        <h2 className="text-2xl sm:text-3xl font-semibold">All Listings</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setShowHomepageOrder(!showHomepageOrder)}
            className={`h-9 px-4 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${showHomepageOrder ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
            data-testid="toggle-homepage-order"
          >
            <Home className="w-3.5 h-3.5" /> Reorder Homepage
          </button>
          {selectedAll.size > 0 && (
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1.5" data-testid="all-bulk-toolbar">
              <span className="text-sm font-medium text-slate-700">{selectedAll.size} selected</span>
              <button onClick={() => handleBulkAction('approve')} disabled={bulkLoading} className="h-8 px-4 rounded-full bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-all flex items-center gap-1 disabled:opacity-50" data-testid="bulk-approve-all">
                <CheckCircle2 className="w-3 h-3" /> Approve
              </button>
              <button onClick={() => handleBulkAction('reject')} disabled={bulkLoading} className="h-8 px-4 rounded-full bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition-all flex items-center gap-1 disabled:opacity-50" data-testid="bulk-reject-all">
                <XCircle className="w-3 h-3" /> Reject
              </button>
              <button onClick={() => handleBulkAction('delete')} disabled={bulkLoading} className="h-8 px-4 rounded-full bg-slate-800 text-white text-xs font-medium hover:bg-slate-900 transition-all flex items-center gap-1 disabled:opacity-50" data-testid="bulk-delete-all">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {showHomepageOrder && <HomepageOrderPanel onClose={() => setShowHomepageOrder(false)} />}

      {/* Search & Filters Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by title, seller, or locality..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all"
              data-testid="listing-search"
            />
          </div>
          <div className="relative min-w-[150px]">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="City..."
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="h-10 pl-10 pr-4 rounded-lg border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all w-full"
              data-testid="listing-city-filter"
            />
          </div>
          <select
            value={propertyTypeFilter}
            onChange={(e) => setPropertyTypeFilter(e.target.value)}
            className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all"
            data-testid="listing-type-filter"
          >
            <option value="">All Types</option>
            <option value="apartment">Apartment</option>
            <option value="villa">Villa</option>
            <option value="land">Land</option>
            <option value="commercial">Commercial</option>
          </select>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`h-10 px-4 rounded-lg border text-sm font-medium flex items-center gap-1.5 transition-all ${showFilters ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
            data-testid="toggle-date-filters"
          >
            <Filter className="w-4 h-4" /> Date
          </button>
          {hasFilters && (
            <button onClick={clearFilters} className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition-all" data-testid="clear-listing-filters">Clear</button>
          )}
        </div>
        {showFilters && (
          <div className="flex gap-3 mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm" data-testid="listing-date-from" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm" data-testid="listing-date-to" />
            </div>
          </div>
        )}
      </div>

      <p className="text-sm text-slate-500 mb-3">{allListings.length} listings{hasFilters ? ' (filtered)' : ''}</p>

      {allListings.length > 1 && (
        <label className="flex items-center gap-2 mb-3 cursor-pointer text-sm text-slate-600">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-slate-300 accent-blue-600"
            checked={selectedAll.size === allListings.length && allListings.length > 0}
            onChange={() => {
              if (selectedAll.size === allListings.length) setSelectedAll(new Set());
              else setSelectedAll(new Set(allListings.map(l => l.id)));
            }}
            data-testid="select-all-listings"
          />
          Select all
        </label>
      )}
      <div className="space-y-4" data-testid="all-listings">
        {allListings.map((listing) => (
          <div key={listing.id} className={`bg-white rounded-2xl border p-6 transition-all ${selectedAll.has(listing.id) ? 'border-blue-400 ring-1 ring-blue-100' : 'border-slate-200'}`} data-testid={`listing-${listing.id}`}>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-start pt-1">
                <input type="checkbox" className="w-4 h-4 rounded border-slate-300 accent-blue-600 cursor-pointer" checked={selectedAll.has(listing.id)} onChange={() => toggleSelect(listing.id)} data-testid={`checkbox-all-${listing.id}`} />
              </div>
              <div className="flex-shrink-0 w-48 h-32 bg-slate-200 rounded-lg overflow-hidden">
                {listing.images && listing.images.length > 0 ? (
                  <img src={resolveImg(listing.images[0])} alt={listing.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Home className="w-12 h-12 text-slate-400" /></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-xl font-semibold">{listing.title}</h3>
                  <div className="flex gap-2 flex-shrink-0">
                    <span className={`text-xs font-medium px-3 py-1 rounded-full ${listing.status === 'approved' ? 'bg-green-100 text-green-700' : listing.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                      {listing.status}
                    </span>
                    {listing.verified && <span className="text-xs font-medium px-3 py-1 rounded-full bg-blue-100 text-blue-700">Verified</span>}
                    {listing.featured && <span className="text-xs font-medium px-3 py-1 rounded-full bg-yellow-100 text-yellow-700">Featured</span>}
                    {listing.paused && <span className="text-xs font-medium px-3 py-1 rounded-full bg-orange-100 text-orange-700" data-testid={`badge-paused-${listing.id}`}>Paused</span>}
                  </div>
                </div>
                <p className="text-slate-600 text-sm mb-3 line-clamp-2">{listing.description}</p>
                <div className="flex gap-4 text-sm text-slate-500 mb-4 flex-wrap">
                  <span className="capitalize">{listing.property_type}</span>
                  <span>•</span>
                  <span>{listing.locality}, {listing.city}</span>
                  <span>•</span>
                  <span className="capitalize">{listing.seller_type}</span>
                  {listing.created_at && <><span>•</span><span>Listed: {new Date(listing.created_at).toLocaleDateString()}</span></>}
                </div>
                <div className="flex flex-wrap gap-2">
                  {listing.status === 'approved' && (
                    <>
                      <button onClick={() => handleAction('verify', listing.id, `?verified=${!listing.verified}`)} className="h-9 px-4 rounded-full bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-all flex items-center gap-1.5" data-testid={`verify-${listing.id}`}>
                        <Shield className="w-3.5 h-3.5" /> {listing.verified ? 'Unverify' : 'Verify'}
                      </button>
                      <button onClick={() => handleAction('feature', listing.id, `?featured=${!listing.featured}`)} className="h-9 px-4 rounded-full bg-yellow-600 text-white text-sm font-medium hover:bg-yellow-700 transition-all flex items-center gap-1.5" data-testid={`feature-${listing.id}`}>
                        <Star className="w-3.5 h-3.5" /> {listing.featured ? 'Unfeature' : 'Feature'}
                      </button>
                      <button onClick={() => handleAction('toggle-featured-project', listing.id)} className={`h-9 px-4 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${listing.featured_project ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-testid={`explore-${listing.id}`}>
                        <Home className="w-3.5 h-3.5" /> {listing.featured_project ? 'On Homepage' : 'Add to Homepage'}
                      </button>
                      <button onClick={() => handleAction('toggle-new-launch', listing.id)} className={`h-9 px-4 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${listing.new_launch ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-testid={`new-launch-${listing.id}`}>
                        <Tag className="w-3.5 h-3.5" /> New Launch
                      </button>
                      <button onClick={() => handleAction('toggle-pause', listing.id)} className={`h-9 px-4 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${listing.paused ? 'bg-orange-600 text-white hover:bg-orange-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-testid={`pause-${listing.id}`}>
                        {listing.paused ? <><PlayCircle className="w-3.5 h-3.5" /> Resume</> : <><PauseCircle className="w-3.5 h-3.5" /> Pause</>}
                      </button>
                    </>
                  )}
                  {listing.status === 'pending' && (
                    <>
                      <button onClick={() => handleAction('approve', listing.id)} className="h-9 px-4 rounded-full bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-all flex items-center gap-1.5" data-testid={`approve-all-${listing.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                      </button>
                      <button onClick={() => handleAction('reject', listing.id)} className="h-9 px-4 rounded-full bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-all flex items-center gap-1.5" data-testid={`reject-all-${listing.id}`}>
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </button>
                    </>
                  )}
                  <button onClick={() => onEditListing(listing)} className="h-9 px-4 rounded-full bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition-all flex items-center gap-1.5" data-testid={`edit-all-${listing.id}`}>
                    <Edit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => handleDelete(listing.id)} className="h-9 px-4 rounded-full bg-slate-100 text-slate-900 text-sm font-medium hover:bg-slate-200 transition-all flex items-center gap-1.5" data-testid={`delete-${listing.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                  <button onClick={() => setExpandedListing(expandedListing === listing.id ? null : listing.id)} className="h-9 px-4 rounded-full bg-slate-100 text-slate-700 text-sm font-medium hover:bg-slate-200 transition-all flex items-center gap-1.5" data-testid={`view-details-all-${listing.id}`}>
                    {expandedListing === listing.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    {expandedListing === listing.id ? 'Hide' : 'Details'}
                  </button>
                </div>
              </div>
            </div>
            {expandedListing === listing.id && <ListingDetailPanel listing={listing} testIdPrefix="all-" />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AllListingsTab;
